<template>
  <div class="oai-IntroStep">
    <div class="oai-IntroStep_Header">
      <div class="oai-IntroStep_Title">
        <i18n-t keypath="onboarding.ai.intro.title_prefix" scope="global">
          <template #brand>
            <span class="oai-IntroStep_TitleBrand">{{ $t('app_name') }}</span>
          </template>
        </i18n-t>
      </div>

      <p class="oai-IntroStep_Subtitle">
        {{ $t('onboarding.ai.intro.subtitle') }}
      </p>
    </div>

    <AppInput
      v-if="isSetupNameNeeded"
      v-model="name"
      :max-length="50"
      :placeholder="$t('onboarding.ai.intro.name_placeholder')"
      autocomplete="name"
      class="oai-IntroStep_Input"
      icon="user-round"
      size="6xlg"
    />

    <div class="oai-IntroStep_CheckboxCard">
      <AppCheckbox v-model="localSubscription" align-start class="oai-IntroStep_Checkbox">
        <span class="oai-IntroStep_CheckboxTitle">
          {{ $t('onboarding.ai.intro.subscription_title') }}
        </span>
        <span class="oai-IntroStep_CheckboxDescription">
          {{ $t('onboarding.ai.intro.subscription_description') }}
        </span>
      </AppCheckbox>
    </div>

    <div class="oai-IntroStep_Actions">
      <AppButtonNext
        :disabled="loading || isNavigating || !canProceed"
        class="oai-IntroStep_PrimaryAction"
        @click="onContinue"
      >
        {{ $t('action.proceed') }}
      </AppButtonNext>

      <p class="oai-IntroStep_Legal">
        <i18n-t keypath="onboarding.ai.intro.legal" scope="global">
          <template #terms>
            <AppHrefLink href="https://oboard.io/legal">
              {{ $t('onboarding.ai.intro.legal.terms') }}
            </AppHrefLink>
          </template>
          <template #privacy>
            <AppHrefLink href="https://oboard.io/privacy-policy">
              {{ $t('onboarding.ai.intro.legal.privacy') }}
            </AppHrefLink>
          </template>
        </i18n-t>
      </p>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useStore } from 'vuex'

import { useOnboardingAiStepContext } from '@/composables/onboarding-ai-step'
import { useOnboardingAiTracking } from '@/composables/onboarding-ai-step'
import { EVENT_NAMES } from '@/tracking/amplitude-helpers'
import { handleError } from '@/utils/error-handling'
import { isStringEmpty } from '@/utils/general'
import { useRenameUser, useSetSubscriptionsForUser } from '@/utils/web-app/composables'
import { PLUGIN_OPTIONS_KEYS } from '@root/template-options-keys'

import AppHrefLink from '@/components/AppHrefLink'
import AppButtonNext from '@/components/ui/AppButtonNext/AppButtonNext'
import AppCheckbox from '@/components/ui/AppCheckbox/AppCheckbox'
import AppInput from '@/components/ui/AppInput/AppInput'

defineOptions({
  name: 'OnboardingAiIntroStep'
})

const emit = defineEmits({
  next: null
})

const localSubscription = ref(true)

const { setSubscriptionsForUser } = useSetSubscriptionsForUser({
  localSubscription
})

const store = useStore()

const { onRenameUser, name, isNameValid } = useRenameUser()

const isSetupNameNeeded = computed(() => store.state.pluginOptions[PLUGIN_OPTIONS_KEYS.SETUP_NAME])
const isNameValidLocal = computed(() => !isStringEmpty(name.value))

const canProceed = computed(() => {
  if (isSetupNameNeeded.value) {
    return isNameValidLocal.value
  }
  return true
})

const stepContext = useOnboardingAiStepContext()
const isNavigating = computed(() => Boolean(stepContext?.navigating))

const loading = ref(false)
const onContinue = async () => {
  try {
    loading.value = true
    await setSubscriptionsForUser()
    if (isSetupNameNeeded.value) {
      await onRenameUser()
      if (!isNameValid.value) return
      onNext()
    } else {
      onNext()
    }
  } catch (error) {
    handleError({ error })
  } finally {
    loading.value = false
  }
}

const onNext = () => {
  emit('next', {
    marketing_opt_in: localSubscription.value
  })
}

const { logEvent } = useOnboardingAiTracking()

onMounted(() => {
  logEvent(EVENT_NAMES.ONBOARDING_WELCOME_VIEWED)
})
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/wizard-steps';
@import '~@/assets/styles/onboarding-ai-step';

.oai-IntroStep {
  @extend %wizard-step-styles;

  --step-max-width: 456px;
  --step-gap: 32px;

  padding-top: 0;
  align-items: center;
  width: 100%;
  max-width: var(--step-max-width);
  margin-inline: auto;
}

.oai-IntroStep_Header {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
}

.oai-IntroStep_Title {
  color: $oai-text-primary;
  font-size: 36px;
  font-style: normal;
  font-weight: fw('bold');
  line-height: 44px;
  letter-spacing: -0.72px;
  margin: 0;
  text-align: left;
}

.oai-IntroStep_TitleBrand {
  font-size: 44px;
  line-height: 44px;
  font-weight: fw('semi-bold');
  color: $oai-text-primary;
  font-style: italic;
  font-family: $oai-font-family;
}

.oai-IntroStep_Subtitle {
  margin: 0;
  max-width: 100%;
  font-size: $fs-14;
  line-height: 20px;
  font-weight: fw('regular');
  color: $oai-text-body;
}

.oai-IntroStep_Input {
  width: 100%;
  --ob-ai-padding-primary: 16px;
  --ob-ai-border-radius-primary: #{$border-radius-md};
  --ob-ai-border-color-primary: #{$primary-color-next};
  --prepend-padding-left: 0;
  --input-with-prepend-padding-left: 16px;
  --ob-ai-box-shadow: #{$oai-input-shadow};
  --ob-ai-prepend-icon-color: #{$oai-text-action};
  --ob-ai-placeholder-color: #{$oai-text-placeholder};
  --ob-ai-input-field-color: #{$oai-text-primary};
}

.oai-IntroStep_CheckboxCard {
  width: 100%;
  padding: 8px;
  border-radius: $border-radius-md;
  background: $oai-bg;
}

.oai-IntroStep_Checkbox {
  --font-size: #{$fs-14};
  --line-height: 20px;
  --gap: 10px;
}

.oai-IntroStep_CheckboxTitle {
  display: block;
  font-weight: fw('semi-bold');
  color: $oai-text-primary;
}

.oai-IntroStep_CheckboxDescription {
  display: block;
  margin-top: 4px;
  font-size: $fs-14;
  line-height: 20px;
  font-weight: fw('regular');
  color: $oai-text-secondary;
}

.oai-IntroStep_Actions {
  display: grid;
  gap: 12px;
  width: 100%;
}

.oai-IntroStep_PrimaryAction {
  width: 100%;
}

.oai-IntroStep_Legal {
  margin: 0;
  font-size: $fs-12;
  line-height: 18px;
  text-align: center;
  color: $oai-text-secondary;
}
</style>
